import { type ReactNode } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td, Tooltip, Skeleton } from '@parrot/ui';
import { Info } from 'lucide-react';
import styles from './LeaderboardTable.module.css';

export type SortDirection = 'asc' | 'desc';

/** Shape of a column's loading placeholder, so the skeleton mirrors the real
 *  content instead of a flat full-width bar. 'media' = icon/poster + name
 *  (the leading link column); 'bar' = a metric bar; 'short' = a narrow value. */
export type SkeletonShape = 'media' | 'bar' | 'short';

export interface LeaderboardColumn<Row> {
  id: string;
  label: ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  /** When set, the header is sortable; this is the key reported to onSortChange. */
  sortKey?: string;
  /** Direction a first click requests (desc suits figures). Default 'asc'. */
  sortFirst?: SortDirection;
  /** Header help tooltip. */
  help?: string;
  /** Loading placeholder shape for this column. Defaults to 'bar'. */
  skeleton?: SkeletonShape;
  /** Cell renderer. `index` is the row's position in the current list. */
  render: (row: Row, index: number) => ReactNode;
}

export interface LeaderboardTableProps<Row> {
  columns: LeaderboardColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row, index: number) => string;
  /** Current sort — `key` matched against each column's `sortKey`. */
  sort?: { key: string; direction: SortDirection };
  onSortChange?: (sort: { key: string; direction: SortDirection }) => void;
  loading?: boolean;
  loadingRows?: number;
  onRowClick?: (row: Row) => void;
}

/**
 * Reusable leaderboard table built on @parrot/ui Table — it provides the header
 * sort states (gold active column, hover, aria-sort, keyboard-accessible sort
 * button) and the row hover treatment. The column registry + cell renderers stay
 * the caller's, so the Titles and Platforms leaderboards share one table while
 * keeping their own columns and cell visuals.
 */
export function LeaderboardTable<Row>({
  columns,
  rows,
  getRowKey,
  sort,
  onSortChange,
  loading = false,
  loadingRows = 8,
  onRowClick,
}: LeaderboardTableProps<Row>) {
  return (
    <Table
      className={styles.table}
      sort={sort}
      onSortChange={onSortChange}
    >
      <Thead>
        <Tr>
          {columns.map((col) => {
            const labelText = typeof col.label === 'string' ? col.label : '';
            return (
              <Th
                key={col.id}
                align={col.align}
                sortKey={col.sortKey}
                sortFirst={col.sortFirst}
                style={col.width ? { width: col.width } : undefined}
              >
                <span className={styles.headCell}>
                  {col.label}
                  {col.help && (
                    <Tooltip
                      title={<span className={styles.tipTitle}>{labelText}</span>}
                      content={<span className={styles.tipBody}>{col.help}</span>}
                      placement={'top'}
                    >
                      <Info size={12} className={styles.helpIcon} />
                    </Tooltip>
                  )}
                </span>
              </Th>
            );
          })}
        </Tr>
      </Thead>
      <Tbody>
        {loading
          ? Array.from({ length: loadingRows }, (_, r) => (
              <Tr key={`skeleton-${r}`}>
                {columns.map((col) => (
                  <Td key={col.id} align={col.align} className={styles.cell}>
                    {col.skeleton === 'media' ? (
                      <span className={styles.skeletonMedia}>
                        <Skeleton shape={'circle'} width={28} height={28} />
                        <Skeleton height={'0.9em'} width={'60%'} />
                      </span>
                    ) : col.skeleton === 'short' ? (
                      <Skeleton height={'0.9em'} width={'40%'} />
                    ) : (
                      <Skeleton height={'0.9em'} width={'75%'} />
                    )}
                  </Td>
                ))}
              </Tr>
            ))
          : rows.map((row, index) => (
              <Tr
                key={getRowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => {
                  const isSorted = col.sortKey !== undefined && col.sortKey === sort?.key;
                  return (
                    <Td
                      key={col.id}
                      align={col.align}
                      style={col.width ? { width: col.width, maxWidth: col.width } : undefined}
                      className={isSorted ? `${styles.cell} ${styles.cellActive}` : styles.cell}
                    >
                      {col.render(row, index)}
                    </Td>
                  );
                })}
              </Tr>
            ))}
      </Tbody>
    </Table>
  );
}
